import { useQuery } from 'data-fetching-client';
import { GET_IN_APP_CURRENCIES, GET_PUBLIC_CURRENCIES } from 'pricing-pages-api/currency/queries';
import { getPreferredLanguage } from 'pricing-pages-lib/utils/getPreferredLanguage';
import { useCallback } from 'react';
import { useGetCurrencyStore, useSetCurrencyStore } from '../../../context/CurrencyContext';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { getCountryCodeFromLocale } from '../../utils/countryCodeUtils';
import { isCurrencyCodeString, isCountryCodeString } from 'pricing-pages-api/currency/types';
import { useIsInAppAuth } from '../user/useUserInfo';
const UseSetLoadingCurrencyComplete = () => {
  const setStore = useSetCurrencyStore();
  const handleCurrencyFetchCompleted = ({
    currencies
  }) => {
    setStore(currencies);
  };
  return handleCurrencyFetchCompleted;
};
export const useCurrencies = () => {
  const isInAppAuth = useIsInAppAuth();
  const handleCurrencyFetchCompleted = UseSetLoadingCurrencyComplete();
  const setStore = useSetCurrencyStore();
  const version = 'v2';
  const browserLocale = getPreferredLanguage() || 'en-us';
  const rawCountryCode = getCountryCodeFromLocale();
  const preferredCountryCode = isCountryCodeString(rawCountryCode) ? rawCountryCode : undefined;
  const handleCompleted = useCallback(data => {
    const queryName = isInAppAuth ? 'GET_IN_APP_CURRENCIES' : 'GET_PUBLIC_CURRENCIES';
    logApiSuccess({
      queryName,
      operation: isInAppAuth ? 'getInAppCurrencies' : 'getPublicCurrencies',
      version
    });
    handleCurrencyFetchCompleted(data);
  }, [handleCurrencyFetchCompleted, version, isInAppAuth]);
  const handleError = useCallback(error => {
    const queryName = isInAppAuth ? 'GET_IN_APP_CURRENCIES' : 'GET_PUBLIC_CURRENCIES';
    logApiError(error, {
      queryName,
      operation: isInAppAuth ? 'getInAppCurrencies' : 'getPublicCurrencies',
      version
    });
    // Populate the store with a USD fallback so downstream queries gated on
    // useIsDisplayedCurrencyCodeReady don't hang forever when the currency
    // RPC fails.
    setStore({
      displayedCurrencyCode: 'USD'
    });
  }, [version, isInAppAuth, setStore]);

  // Separate queries for in-app and public contexts to ensure type safety
  const {
    data: inAppData,
    error: inAppError,
    loading: inAppLoading
  } = useQuery(GET_IN_APP_CURRENCIES, {
    variables: {},
    skip: !isInAppAuth,
    onCompleted: handleCompleted,
    onError: handleError
  });
  const {
    data: publicData,
    error: publicError,
    loading: publicLoading
  } = useQuery(GET_PUBLIC_CURRENCIES, {
    variables: {
      browserLocale,
      preferredCountryCode
    },
    skip: isInAppAuth,
    onCompleted: handleCompleted,
    onError: handleError
  });
  const data = isInAppAuth ? inAppData : publicData;
  const error = isInAppAuth ? inAppError : publicError;
  const loading = isInAppAuth ? inAppLoading : publicLoading;
  const currencyData = data && data.currencies;
  return {
    currencyData,
    error,
    loading
  };
};
export const useDisplayedCurrencyCode = () => {
  const currencyCode = useGetCurrencyStore(store => store.displayedCurrencyCode);
  if (isCurrencyCodeString(currencyCode)) {
    return currencyCode;
  }
  return 'USD';
};

// True once the currency RPC has populated the store. Before then,
// useDisplayedCurrencyCode falls back to 'USD' — callers that fire network
// requests keyed on currency should wait for this to avoid a USD→real-currency
// refetch.
export const useIsDisplayedCurrencyCodeReady = () => {
  const currencyCode = useGetCurrencyStore(store => store.displayedCurrencyCode);
  return isCurrencyCodeString(currencyCode);
};
export const useLockedCurrencyCode = () => {
  const currencyCode = useGetCurrencyStore(store => store.lockedCurrencyCode);
  return currencyCode;
};
export const useCurrencyCodeUnitToDisplay = () => {
  const currencyCodeUnit = useGetCurrencyStore(store => store.currencyCodeUnit);
  const showCurrencyCodeUnit = useGetCurrencyStore(store => store.showCurrencyCodeUnit);
  return showCurrencyCodeUnit ? currencyCodeUnit : undefined;
};
export const useAvailableCurrencyCodes = () => {
  const availableCurrencyCodes = useGetCurrencyStore(store => store.availableCurrencyCodes);
  return availableCurrencyCodes.filter(isCurrencyCodeString);
};
export const useCountryCode = () => {
  const countryCode = useGetCurrencyStore(store => store.countryCode);
  return countryCode || undefined;
};